/**
 * Animates slide up.
 *
 * Replacement for jQuery slideUp animation.
 *
 * @param element           The element to slide up.
 * @param [duration=400]    The duration of the animation in milliseconds.
 * @param [display="block"] The elements initial display property.
 */
const slideUp = (element: HTMLElement, duration = 400, display = 'block'): void => {
	// Make the element visible and reset its height
	element.style.display = display;
	const height = element.scrollHeight;

	// Set the element's height to its current pixel height, overflow hidden, and transition properties
	if (element.style.height !== 'auto') {
		element.style.height = 'auto';
	}

	element.style.height = height + 'px';
	element.style.transition = `height ${duration}ms ease-out`;
	element.style.overflow = 'hidden';

	void element.offsetHeight;

	// Trigger reflow to apply the height style before changing it
	requestAnimationFrame(() => {
		element.style.height = '0';
	});

	// Listen for transition end to reset styles and hide the element
	element.addEventListener('transitionend', function handler() {
		element.style.display = 'none';
		element.style.height = '0';
		element.style.removeProperty('overflow');
		element.style.removeProperty('transition');
		element.removeEventListener('transitionend', handler);
		window.dispatchEvent(new Event('slideAnimationEnd'));
	});
};

/**
 * Animate slide down
 *
 * Replacement for jQuery slideDown animation.
 *
 * @param element           The element to slide down.
 * @param [duration=400]    The duration of the animation in milliseconds.
 * @param [display="block"] The elements initial display property.
 */
const slideDown = (element: HTMLElement, duration = 400, display = 'block'): void => {
	element.style.removeProperty('display');
	const computedDisplay = window.getComputedStyle(element).display;
	if (computedDisplay === 'none') {
		element.style.display = display;
	}

	// Get the full height of the element's content
	const height = element.scrollHeight;

	// Set the height to 0 initially and then animate to the full height
	element.style.height = '0';
	element.style.overflow = 'hidden';
	element.style.transition = `height ${duration}ms ease-out`;

	void element.offsetHeight;

	// Trigger reflow to apply the initial height style
	requestAnimationFrame(() => {
		element.style.height = height + 'px';
	});

	// Listen for transition end to reset styles
	element.addEventListener('transitionend', function handler() {
		element.style.display = display;
		element.style.height = 'auto';
		element.style.removeProperty('overflow');
		element.style.removeProperty('transition');
		element.removeEventListener('transitionend', handler);
		window.dispatchEvent(new Event('slideAnimationEnd'));
	});
};

const getOffsetTop = (element: HTMLElement | null) => {
	let offsetTop = 0;
	while (element) {
		offsetTop += element.offsetTop;
		element = element.offsetParent as HTMLElement;
	}
	return offsetTop;
};

interface Options {
	element?: string;
	class?: string;
	callback?: (() => void) | null;
}

const breakElementLines = (options: Options = {}) => {
	const defaults = {
		element: '.has-rainbow-underline',
		class: 'underline',
		callback: null,
	};

	options = Object.assign({}, defaults, options);
	if (!options.element) {
		return;
	}
	const elements = Array.from(document.querySelectorAll(options.element));

	elements.forEach((element) => {
		if (element.textContent) {
			const parentNode = element.parentNode;
			if (parentNode && options.element) {
				let parent = parentNode as HTMLElement;
				const parentTag = parent.tagName.toLowerCase();
				if ('mark' === parentTag || 'em' === parentTag) {
					parent = parent.parentNode as HTMLElement;
				}
				parent.classList.add('parent--' + options.element.split('.').join(''));
			}

			if (element.querySelector('a')) {
				element = element.querySelector('a') as HTMLElement;
			}

			const textContent = element.textContent ?? '';
			const text = textContent.split(' ');
			element.innerHTML = '<span>' + text.join('</span> <span>') + '</span>';
			let spanOffset: boolean | number = false;
			let newlineContent = '';
			const lines = [];

			const spans = Array.from(element.querySelectorAll('span'));
			spans.forEach((span) => {
				const currentSpanOffset = span.offsetTop;
				if (spanOffset === false) spanOffset = currentSpanOffset;

				if (currentSpanOffset !== spanOffset && newlineContent) {
					lines.push(newlineContent.trim());
					newlineContent = '';
					spanOffset = currentSpanOffset;
				}

				newlineContent += span.textContent + ' ';
			});

			lines.push(newlineContent.trim());
			element.innerHTML =
				'<span class="' +
				options.class +
				'">' +
				lines.join('</span> <span class="' + options.class + '">') +
				'</span>';
		}
	});

	if (options.callback) options.callback();
};

const isTouchDevice = () => {
	const userAgent = navigator.userAgent || navigator.vendor;
	return /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(
		userAgent.toLowerCase()
	);
};

const isMobileViewport = (breakpoints: IBreakpoints) => {
	const mediaQuery = breakpoints
		? `(max-width: ${parseInt(breakpoints.nav) - 1}px)`
		: '(max-width: 1239px)';
	return window.matchMedia(mediaQuery).matches;
};

const setHiddenSlides = (swiper: typeof window.Swiper) => {
	const slidesVisible = Math.floor(swiper.params.slidesPerView);

	const allSlides = swiper.el.querySelectorAll('.swiper-slide');
	allSlides.forEach((slide: HTMLElement) => {
		slide.setAttribute('tabindex', '-1');
		slide.setAttribute('aria-hidden', 'true');
		slide.querySelectorAll<HTMLElement>('a, button').forEach((item) => {
			item.setAttribute('tabindex', '-1');
		});
	});

	let visibleSlide = swiper.el.querySelector('.swiper-slide-active') as HTMLElement | null;
	for (let i = slidesVisible; i > 0; i--) {
		if (null === visibleSlide) {
			break;
		}

		visibleSlide.setAttribute('aria-hidden', 'false');
		visibleSlide.setAttribute('tabindex', '-1');
		visibleSlide
			.querySelectorAll<HTMLElement>('a, button')
			.forEach((item) => item.removeAttribute('tabindex'));

		visibleSlide = visibleSlide.nextElementSibling as HTMLElement | null;
	}
};

export {
	slideUp,
	slideDown,
	getOffsetTop,
	isTouchDevice,
	isMobileViewport,
	breakElementLines,
	setHiddenSlides,
};
